// Utility functions for detecting and formatting JSON content

/**
 * Finds JSON objects/arrays in text by matching opening and closing brackets
 */
function findJsonBoundaries(text: string): { start: number; end: number; type: 'object' | 'array' }[] {
  const results: { start: number; end: number; type: 'object' | 'array' }[] = [];

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '{' || char === '[') {
      const isObject = char === '{';
      const openBracket = char;
      const closeBracket = isObject ? '}' : ']';

      let bracketCount = 1;
      let inString = false;
      let escaped = false;

      for (let j = i + 1; j < text.length; j++) {
        const currentChar = text[j];

        if (escaped) {
          escaped = false;
          continue;
        }

        if (currentChar === '\\') {
          escaped = true;
          continue;
        }

        if (currentChar === '"' && !escaped) {
          inString = !inString;
          continue;
        }

        if (!inString) {
          if (currentChar === openBracket) {
            bracketCount++;
          } else if (currentChar === closeBracket) {
            bracketCount--;

            if (bracketCount === 0) {
              results.push({
                start: i,
                end: j + 1,
                type: isObject ? 'object' : 'array'
              });
              break;
            }
          }
        }
      }
    }
  }

  return results;
}

/**
 * Checks if a string contains valid JSON by analyzing bracket structure
 */
export function isJsonContent(content: string): boolean {
  const trimmed = content.trim();

  // Try to parse the entire content as JSON first
  try {
    const parsed = JSON.parse(trimmed);
    // Don't treat simple citation references like [1], [2] as JSON
    // These are typically markdown citation references
    if (Array.isArray(parsed) && parsed.length === 1 && typeof parsed[0] === 'number') {
      return false;
    }
    return true;
  } catch {
    // Look for JSON boundaries anywhere in the content
    const boundaries = findJsonBoundaries(trimmed);

    if (boundaries.length > 0) {
      // Check if we can find valid JSON anywhere in the content
      for (const boundary of boundaries) {
        try {
          const candidate = trimmed.substring(boundary.start, boundary.end);
          const parsed = JSON.parse(candidate);
          // Don't treat simple citation references like [1], [2] as JSON
          if (Array.isArray(parsed) && parsed.length === 1 && typeof parsed[0] === 'number') {
            continue;
          }
          return true; // Found valid JSON
        } catch {
          continue;
        }
      }
    }
    return false;
  }
}

/**
 * Extracts JSON from content using robust bracket matching
 */
export function extractJsonFromContent(content: string): { json: any; hasOtherContent: boolean; remainingText?: string } | null {
  const trimmed = content.trim();

  // Try to parse the entire content as JSON first
  try {
    const json = JSON.parse(trimmed);
    // Don't treat simple citation references like [1], [2] as JSON
    if (Array.isArray(json) && json.length === 1 && typeof json[0] === 'number') {
      return null;
    }
    return { json, hasOtherContent: false };
  } catch {
    // Find all JSON boundaries in the content
    const boundaries = findJsonBoundaries(trimmed);

    for (const boundary of boundaries) {
      const jsonCandidate = trimmed.substring(boundary.start, boundary.end);
      try {
        const json = JSON.parse(jsonCandidate);
        // Don't treat simple citation references like [1], [2] as JSON
        if (Array.isArray(json) && json.length === 1 && typeof json[0] === 'number') {
          continue;
        }

        // Extract remaining text (before + after the JSON)
        const beforeJson = trimmed.substring(0, boundary.start).trim();
        const afterJson = trimmed.substring(boundary.end).trim();
        const remainingText = [beforeJson, afterJson].filter(text => text.length > 0).join('\n\n');

        return {
          json,
          hasOtherContent: remainingText.length > 0,
          remainingText: remainingText.length > 0 ? remainingText : undefined
        };
      } catch {
        // Continue to next boundary if this one fails to parse
        continue;
      }
    }
  }

  return null;
}

/**
 * Formats JSON data as user-friendly HTML
 */
export function formatJsonAsHtml(json: any, title?: string): string {
  const formatValue = (value: any, depth = 0): string => {
    const indent = '  '.repeat(depth);
    const nextIndent = '  '.repeat(depth + 1);

    if (value === null) return '<span style="color: #666;">null</span>';
    if (value === undefined) return '<span style="color: #666;">undefined</span>';

    if (typeof value === 'string') {
      return `<span style="color: #22863a;">"${escapeHtml(value)}"</span>`;
    }

    if (typeof value === 'number') {
      return `<span style="color: #005cc5;">${value}</span>`;
    }

    if (typeof value === 'boolean') {
      return `<span style="color: #d73a49;">${value}</span>`;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return '<span style="color: #666;">[]</span>';

      const items = value.map(item => `${nextIndent}${formatValue(item, depth + 1)}`).join(',<br>');
      return `[<br>${items}<br>${indent}]`;
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return '<span style="color: #666;">{}</span>';

      const items = keys.map(key => {
        const formattedKey = `<span style="color: #22863a;">"${escapeHtml(key)}"</span>`;
        return `${nextIndent}${formattedKey}: ${formatValue(value[key], depth + 1)}`;
      }).join(',<br>');

      return `{<br>${items}<br>${indent}}`;
    }

    return String(value);
  };

  const formattedJson = formatValue(json);

  return `
    <div style="
      background-color: #f8f9fa;
      border: 1px solid #e1e4e8;
      border-radius: 6px;
      padding: 16px;
      margin: 8px 0;
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
      font-size: 14px;
      line-height: 1.45;
      color: #24292e;
      overflow-x: auto;
    ">
      ${title ? `<div style="font-weight: 600; margin-bottom: 12px; color: #0366d6;">${escapeHtml(title)}</div>` : ''}
      <pre style="margin: 0; white-space: pre-wrap; word-wrap: break-word;">${formattedJson}</pre>
    </div>
  `;
}

/**
 * Escapes HTML characters in a string
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Formats task creation response specifically
 */
export function formatTaskResponse(response: any): string {
  if (response.task) {
    const task = response.task;
    const createdAt = new Date(task.created_at).toLocaleString();

    return `
      <div style="
        background-color: #d4edda;
        border: 1px solid #c3e6cb;
        border-radius: 6px;
        padding: 16px;
        margin: 8px 0;
        color: #155724;
      ">
        <div style="font-weight: 600; margin-bottom: 8px; display: flex; align-items: center;">
          ✅ Task Created Successfully
        </div>
        <div style="margin-bottom: 12px;">
          <strong>${escapeHtml(task.title)}</strong>
        </div>
        <div style="margin-bottom: 8px; color: #6c757d;">
          ${escapeHtml(task.description)}
        </div>
        <div style="font-size: 12px; color: #6c757d;">
          <div>Priority: <span style="text-transform: capitalize;">${task.priority}</span></div>
          <div>Status: <span style="text-transform: capitalize;">${task.status}</span></div>
          <div>Created: ${createdAt}</div>
          ${task.tags && task.tags.length > 0 ? `<div>Tags: ${task.tags.map((tag: string) => `<span style="background: #e9ecef; padding: 2px 6px; border-radius: 3px; margin-right: 4px;">${escapeHtml(tag)}</span>`).join('')}</div>` : ''}
        </div>
      </div>
    `;
  }

  return formatJsonAsHtml(response, 'Response');
}